import { zodResolver } from '@hookform/resolvers/zod';
import { AircraftType } from '@pelicargo/types';
import { DayOfWeek, InstructionUsage } from '@prisma/client';
import { useForm, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

import { TrpcInputs, TrpcOutputs } from '../../config/trpc';
import { addressOptional } from './useAddressForm';

export type InstructionInput = NonNullable<TrpcInputs['createInstruction']>;

export type InstructionOutput = TrpcOutputs['createInstruction'];

export type InstructionFormValues = z.infer<typeof validationSchema>;

type UseInstructionForm = { methods: UseFormReturn<InstructionFormValues> };

export const address = z.object({
  recipient: z.string().nullish(),
  line_one: z.string().nullish(),
  line_two: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  zip_code: z.string().nullish(),
  country: z.string().nullish(),
});

const baseSelectors = z.object({
  provider_id: z.string().min(1, 'Required'),
  airport_id: z.string().min(1, 'Required'),
  aircraft_type: z.nativeEnum(AircraftType).nullish(),
  flight_number: z.string().nullish(),
  airline_product: z.string().nullish(),
});

export const validationSchema = z.discriminatedUnion('usage', [
  baseSelectors.extend({
    usage: z.literal(InstructionUsage.QUOTING),
    booking_instructions: z.string().nullable(),
    dropoff_instructions: z.string().nullable(),
    delivery_address: addressOptional,
    dropoff_contact_id: z.coerce.number().nullish(),
    from_dock_hours: z.coerce.number().nullish(),
    to_dock_hours: z.coerce.number().nullish(),
  }),
  baseSelectors.extend({
    usage: z.literal(InstructionUsage.BOOKING),
    cutoff_at_hours_before: z.coerce.number().nullish(),
    cutoff_at_time: z.string().time().nullish(),
    cutoff_at_time_days_before: z.coerce.number().nullish(),
    schedule: z.array(z.nativeEnum(DayOfWeek)).nullish(),
  }),
]);

export const useInstructionForm = (data?: Partial<InstructionFormValues>): UseInstructionForm => {
  const { reset, getValues, setValue, ...methods } = useForm<InstructionFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(validationSchema),
    defaultValues: { usage: InstructionUsage.QUOTING, ...data },
  });

  return { methods: { ...methods, getValues, setValue, reset } };
};
